import { useState } from 'react';
import { Link } from 'react-router-dom';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import WatchAnswers from '../../components/WatchAnswers.jsx';
import StatusWord, { ExpectedWord, SeverityWord } from '../../components/StatusWord.jsx';
import { api } from '../../lib/api.js';
import { answerAsAdmin } from '../../lib/watch-admin-answer.js';
import { ANSWER_LABEL, CATEGORY_LABELS, SEVERITY_LABEL, SEVERITY_ORDER, activityPath, formatWhen } from '../../lib/watch-format.js';

// The Events tab of Activity (ENTRAID-347 redesign, mockup 4): Watch events and the daily collection emails of the last
// 30 days on ConsoleTable. Columns When (with the source under it), What happened (with the category), Who, Severity
// and Answer. Severity and the answer are a dot plus an ink word; the event opens its own page.
// 2026-10-29 feedback: an admin answers Expected / Not expected from the row (B3, the same API as the event page); the
// list opens on what needs an answer, Not answered and Not expected (B5); informational events are hidden until "Show
// informational" is on (B6). The filters are remembered per browser, as a convenience only.

const SOURCE_NAME = { daily: 'Daily collection', live: 'Live watch' };

export const FILTERS_KEY = 'onguard.activity.eventsFilters';
export const DEFAULT_VIEW = Object.freeze({ filters: Object.freeze({ answer: Object.freeze(['unanswered', 'no']) }), showInfo: false });
const ANSWER_VALUES = ['unanswered', 'yes', 'no', 'aged-out'];
const isInfo = (r) => r.source === 'live' && r.severity === 'info';

// The remembered view, validated: anything unexpected (or storage the browser refuses) is the default.
export function readView() {
  try {
    const v = JSON.parse(localStorage.getItem(FILTERS_KEY) ?? 'null');
    if (v === null || typeof v !== 'object' || Array.isArray(v)) return DEFAULT_VIEW;
    const f = v.filters !== null && typeof v.filters === 'object' && !Array.isArray(v.filters) ? v.filters : {};
    const filters = {};
    if (['live', 'daily'].includes(f.source)) filters.source = f.source;
    if (SEVERITY_ORDER.includes(f.severity)) filters.severity = f.severity;
    if (Array.isArray(f.answer)) filters.answer = [...new Set(f.answer.filter((a) => ANSWER_VALUES.includes(a)))];
    return { filters, showInfo: v.showInfo === true };
  } catch {
    return DEFAULT_VIEW;
  }
}
function writeView(view) {
  try { localStorage.setItem(FILTERS_KEY, JSON.stringify(view)); } catch { /* the choice is only a convenience */ }
}

// B3: the row can be answered here when it is a Watch event still waiting for an answer and not flagged.
const answerable = (r) => r.source === 'live' && r.answer === 'unanswered' && !r.event?.caseId && r.event?.noticeClass !== 'admin-notice';

function AnswerActions({ row, busy, onAnswer, className = '' }) {
  if (!answerable(row)) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`.trim()} role="group" aria-label={`Answer ${row.what}`}>
      <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={busy} aria-label={`Expected: ${row.what}`} onClick={() => onAnswer(row, 'yes')}>Expected</button>
      <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={busy} aria-label={`Not expected: ${row.what}`} onClick={() => onAnswer(row, 'no')}>Not expected</button>
    </div>
  );
}

function Detail({ row, ruleLabel, customerId, actions }) {
  if (row.source === 'daily') {
    const events = row.history.events ?? [];
    return (
      <p className="text-sm text-ipc-ink">
        {row.history.isTest ? 'A test email you asked for.' : (events.length ? events.map((e) => `${ruleLabel(e.eventId)} x${e.count}`).join(', ') : 'No events.')}
      </p>
    );
  }
  const e = row.event;
  return (
    <div className="space-y-1 text-sm text-ipc-ink">
      <p><span className="text-ipc-muted">Who:</span> {e.actor?.displayName ?? 'Unknown actor'}</p>
      <p><span className="text-ipc-muted">Category:</span> {CATEGORY_LABELS[e.category] ?? e.category}</p>
      {e.witness === 'none' ? <p><StatusWord tone="warn" strong={false}>No independent recipient</StatusWord></p> : null}
      {e.caseId ? <p>Flagged for follow-up.</p> : null}
      {e.answerState === 'aged-out' ? <p>Aged out: nobody answered within {e.agedOut?.days ?? 'the configured number of'} days, so onGuard closed the question. Not counted as expected.</p> : null}
      <WatchAnswers answers={e.answers} resolved={e.resolved} />
      {/* The Answer column is hidden on small screens, so the actions show here there. */}
      {actions ? <div className="md:hidden">{actions}</div> : null}
      <Link className="font-semibold text-ipc-light hover:underline" to={activityPath(customerId, `events/${encodeURIComponent(e.eventId)}`)}>Open the event page</Link>
    </div>
  );
}

/** The answer of a row in words: not answered and not expected carry a dot; a flagged event says so; Expected a check. */
export function AnswerWord({ row }) {
  if (row.event?.caseId) return <StatusWord tone="bad">Flagged</StatusWord>;
  if (!row.answer) return null;
  const word = ANSWER_LABEL[row.answer] ?? row.answer;
  if (row.answer === 'unanswered') return <StatusWord tone="warn">{word}</StatusWord>;
  if (row.answer === 'no') return <StatusWord tone="bad">{word}</StatusWord>;
  // Aged out is not an answer: muted, never shown like Expected.
  if (row.answer === 'aged-out') return <span className="text-ipc-muted">{word}</span>;
  if (row.answer === 'yes') return <ExpectedWord suffix={row.event?.markedExpected ? ' (admin)' : ''}>{word}</ExpectedWord>;
  return <span className="text-ipc-ink">{word}</span>;
}

// A row with the answer the admin just gave applied on top (optimistic until the portal confirms, B3).
function withPatch(r, patch) {
  if (!patch) return r;
  const event = { ...r.event, ...patch };
  return { ...r, event, answer: event.answerState ?? r.answer };
}

export default function EventsFeed({ customerId, rows: given, loading = false, error = null, ruleLabel = (id) => id, initialView }) {
  const [view, setView] = useState(() => initialView ?? readView());
  const [patches, setPatches] = useState({});
  const [pending, setPending] = useState({});
  const [rowError, setRowError] = useState({});
  const [said, setSaid] = useState('');

  const update = (next) => { setView(next); writeView(next); };
  const all = given.map((r) => withPatch(r, r.eventId ? patches[r.eventId] : null));
  const infoCount = all.filter(isInfo).length;
  // B6: informational events (a security info prompt with no new method, Microsoft provisioning) are noise for a team.
  const rows = view.showInfo ? all : all.filter((r) => !isInfo(r));

  async function onAnswer(row, answer) {
    const id = row.eventId;
    if (pending[id]) return;
    setPending((p) => ({ ...p, [id]: true }));
    setRowError((e) => ({ ...e, [id]: null }));
    // Optimistic: the row shows the answer at once and goes back if the portal refuses it.
    setPatches((p) => ({ ...p, [id]: { answerState: answer } }));
    try {
      const changed = await answerAsAdmin(api, customerId, id, answer);
      setPatches((p) => ({ ...p, [id]: changed }));
      setSaid(`Saved: ${ANSWER_LABEL[answer]} for ${row.what}.`);
    } catch (e) {
      setPatches((p) => { const { [id]: _drop, ...rest } = p; return rest; });
      setRowError((x) => ({ ...x, [id]: e?.message || 'The answer could not be recorded.' }));
      setSaid('');
    } finally {
      setPending((p) => { const { [id]: _drop, ...rest } = p; return rest; });
    }
  }

  const actionsOf = (r) => <AnswerActions row={r} busy={Boolean(pending[r.eventId])} onAnswer={onAnswer} />;
  const errorOf = (r) => (r.eventId && rowError[r.eventId] ? <p className="text-xs text-ipc-bad" role="alert">{rowError[r.eventId]}</p> : null);

  const severities = SEVERITY_ORDER.filter((s) => rows.some((r) => r.severity === s));
  const unanswered = rows.filter((r) => r.answer === 'unanswered').length;
  const agedOut = rows.filter((r) => r.answer === 'aged-out').length;
  const columns = [
    {
      id: 'when',
      header: 'When',
      sortValue: (r) => Date.parse(r.when) || 0,
      cell: (r) => (
        <div>
          <span className="whitespace-nowrap text-ipc-ink">{formatWhen(r.when)}</span>
          <div className="text-[12.5px] text-ipc-muted">{SOURCE_NAME[r.source]}</div>
        </div>
      ),
    },
    {
      id: 'what',
      header: 'What happened',
      sortValue: (r) => r.what.toLowerCase(),
      cell: (r) => (
        <div className="min-w-0">
          {r.eventId
            ? <Link className="break-words font-semibold text-ipc-light hover:underline" to={activityPath(customerId, `events/${encodeURIComponent(r.eventId)}`)}>{r.what}</Link>
            : <span className="break-words font-semibold text-ipc-ink">{r.what}</span>}
          {r.event?.category ? <div className="text-[12.5px] text-ipc-muted">{CATEGORY_LABELS[r.event.category] ?? r.event.category}</div> : null}
        </div>
      ),
    },
    { id: 'who', header: 'Who', hideBelow: 'lg', cell: (r) => (r.event ? (r.event.actor?.displayName ?? 'Unknown actor') : null) },
    { id: 'severity', header: 'Severity', sortValue: (r) => SEVERITY_ORDER.indexOf(r.severity), cell: (r) => <SeverityWord severity={r.severity} /> },
    {
      id: 'answer',
      header: 'Answer',
      hideBelow: 'md',
      cell: (r) => (
        <div className="space-y-1">
          <AnswerWord row={r} />
          {actionsOf(r)}
          {errorOf(r)}
        </div>
      ),
    },
  ];
  const filters = [
    { id: 'source', label: 'Source', options: [{ value: 'live', label: 'Live watch' }, { value: 'daily', label: 'Daily collection' }], test: (r, v) => r.source === v },
    { id: 'severity', label: 'Severity', options: severities.map((s) => ({ value: s, label: SEVERITY_LABEL[s] })), test: (r, v) => r.severity === v },
    { id: 'answer', label: 'Answer', multiple: true, options: [
        { value: 'unanswered', label: unanswered > 0 ? `Not answered ${unanswered}` : 'Not answered' },
        { value: 'yes', label: 'Expected' },
        { value: 'no', label: 'Not expected' },
        { value: 'aged-out', label: agedOut > 0 ? `Aged out ${agedOut}` : 'Aged out' },
      ],
      // Only an answerable row is filtered by its answer: a row with none (a daily collection email) always passes, so
      // the default view never empties the list of someone who has only those (an operator account).
      test: (r, v) => r.answer == null || r.answer === v },
  ];
  const showInfo = infoCount > 0 || view.showInfo ? (
    <button type="button" className="btn-secondary px-2.5 py-1 text-xs" aria-pressed={view.showInfo} onClick={() => update({ ...view, showInfo: !view.showInfo })}>
      {view.showInfo ? `Hide informational (${infoCount})` : `Show informational (${infoCount})`}
    </button>
  ) : null;
  return (
    <>
      <p className="sr-only" role="status" aria-live="polite">{said}</p>
      <ConsoleTable
        label="Events"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        rowName={(r) => r.what}
        searchText={(r) => `${r.what} ${SOURCE_NAME[r.source]} ${r.severity ?? ''} ${r.event?.actor?.displayName ?? ''}`}
        searchPlaceholder="Search events"
        filters={filters}
        filterValues={view.filters}
        onFilterChange={(filtersNext) => update({ ...view, filters: filtersNext })}
        actions={showInfo}
        defaultSort={{ columnId: 'when', direction: 'descending' }}
        renderExpanded={(r) => <Detail row={r} ruleLabel={ruleLabel} customerId={customerId} actions={answerable(r) ? <>{actionsOf(r)}{errorOf(r)}</> : null} />}
        loading={loading}
        error={error}
        emptyState={{ title: 'Nothing yet', body: 'Watch events and daily collection emails from the last 30 days appear here.' }}
        framed={false}
      />
    </>
  );
}
